import { invalidate } from '@react-three/fiber';
import { t, type TFunction } from 'i18next';
import {
    Box3,
    BufferAttribute,
    BufferGeometry,
    Matrix4 as Matrix4Three,
    MathUtils,
    Mesh,
    Plane,
    Quaternion,
    Ray,
    Vector2,
    Vector3,
    type WebGLRenderer
} from 'three';

import {
    SettingModelSettingType,
    type ConnectorSnapPoint,
    type ConnectorSnapRange,
    type PartPlacement,
    type PrefabModel
} from 'api/models';
import {
    cloneInstanceParts,
    getArticleId,
    isFramiClamp,
    isFramiWedgeClamp,
    isSupportingConstruction,
    mapPartsArrayToRecord,
    updateFullQuaternion
} from 'app/modules/wallforming/components/scenes/objects/articles/utils/articleUtils';
import { decomposeRollAroundAxis } from 'app/modules/wallforming/components/scenes/objects/articles/utils/transformationUtils';
import { WorkingPlaneData } from 'app/modules/wallforming/components/scenes/objects/helpers/types/workingPlaneData';
import { POIFilterType } from 'app/modules/wallforming/components/scenes/objects/pois/types/poiFilterType';
import {
    getRelatedPoiIndices,
    isPlanarFacePoi
} from 'app/modules/wallforming/components/scenes/objects/pois/utils/poiUtils';
import { getCanvasPointer } from 'app/modules/wallforming/components/scenes/objects/utils/pointerUtils';
import {
    SNAP_POINT_CAPTURE_FACTOR,
    SNAP_RELEASE_FACTOR,
    SNAP_SWITCH_FACTOR
} from 'app/modules/wallforming/components/scenes/objects/utils/screenSpaceUtils';
import { InteractionMode } from 'app/modules/wallforming/components/scenes/types/interactionMode';
import { usePlacementStore } from 'app/modules/wallforming/components/state-management/placement';
import { syncDragStateOnPoiChange } from 'app/modules/wallforming/components/state-management/placement/slices/articles/articleEventHandlerSlice';
import { type DraggingBoxWorkerInput, postDraggingBoxTask } from 'app/modules/wallforming/components/workers';
import { EMPTY_GUID, EPSILON } from 'shared/constants';
import { toastNotifications } from 'shared/utils/toastNotifications';

import type { ArticleDragReference } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleDragReference';
import type { ArticleInstance } from 'app/modules/wallforming/components/scenes/objects/articles/types/articleInstance';
import type { Part } from 'app/modules/wallforming/components/scenes/objects/articles/types/part';
import type { DraggingBox } from 'app/modules/wallforming/components/scenes/objects/helpers/types/draggingBox';
import type { DraggingBoxStatus } from 'app/modules/wallforming/components/scenes/objects/helpers/types/draggingBoxStatus';
import type { SnapPointFeedback } from 'app/modules/wallforming/components/scenes/objects/helpers/types/snapPointFeedback';
import type { PlacementState } from 'app/modules/wallforming/components/state-management/placement/types';
import type { RefObject } from 'react';

/* variables */
const ALIGNMENT_PARALLEL_THRESHOLD = 0.999; // Threshold for parallel vectors

const TOP_BOTTOM_VIEW_THRESHOLD = 0.95; // cos of ~18°

export const instanceToArticleIdMap = new Map<string, string>();
export const instanceToPoiIndexMap = new Map<string, number>();

export const draggingBoxStatus: DraggingBoxStatus = {
    isComputed: false,
    isPending: false
};

export const dragColliderCache = new Map<string, Vector3[]>();
// Instances moved by the active drag; snap lookups must never target them.
export const draggedSnapRelationIds = new Set<string>();

export function syncDraggedSnapRelationIds(instanceIds: readonly string[]): void {
    if (
        draggedSnapRelationIds.size === instanceIds.length &&
        instanceIds.every((instanceId) => draggedSnapRelationIds.has(instanceId))
    ) {
        return;
    }
    draggedSnapRelationIds.clear();
    for (const instanceId of instanceIds) {
        draggedSnapRelationIds.add(instanceId);
    }
}

export const perInstanceDragOffsets = new Map<string, Vector3>();
export const perInstancePlanes = new Map<string, Plane>();
export const perInstancePrevIntersections = new Map<string, Vector3>();

export const dragDelta = new Vector3();
export const anchorPosition = new Vector3();
export const targetPosition = new Vector3();
export const initialPoint = new Vector3();
export const adjustedSnapSource = new Vector3();

export const raycastPlane = new Plane();
export const dragAnchorPositionRef: { current: Vector3 | null } = { current: null };

export const activePoiGuids = new Set<string>();

const instanceDragDelta = new Vector3();
const perInstancePosition = new Vector3();
const perInstanceAlignment = new Vector3();
const projectedSnapDelta = new Vector3();
const wallIntersectionPoint = new Vector3();
const wallCenter = new Vector3();
const boundingBoxHelper = new Box3();
const cursorPointer = new Vector2();

export function updateCursorRay(state: PlacementState): boolean {
    const canvas = state.mainWebGLRendererRef.current?.domElement;
    const camera = state.mainCameraControlsRef.current?.camera;
    const raycaster = state.mainRaycasterRef.current;
    if (!canvas || !camera || !raycaster || !getCanvasPointer(canvas, cursorPointer)) {
        return false;
    }

    camera.updateMatrixWorld();
    raycaster.setFromCamera(cursorPointer, camera);
    return true;
}

/* dragging */
export function getAnchorPosition(
    instance: ArticleInstance,
    target: Vector3,
    mainPartName?: string,
    targetPartName?: string
): boolean {
    const anchorPart =
        (targetPartName ? instance.parts[targetPartName] : undefined) ??
        (mainPartName ? instance.parts[mainPartName] : undefined);
    if (anchorPart) {
        target.copy(anchorPart.position);
        return true;
    }

    const parts = Object.values(instance.parts);
    if (parts.length === 0) {
        return false;
    }

    target.set(0, 0, 0);
    for (const part of parts) {
        target.add(part.position);
    }

    target.divideScalar(parts.length);
    return true;
}

export function getConnectorMainPartName(
    articleId: string | undefined,
    dragRefsMap: Map<string, ArticleDragReference>
): string | undefined {
    if (!articleId) {
        return undefined;
    }

    const articleReference = dragRefsMap.get(articleId)?.articleRef.current;
    if (!articleReference || !isFramiClamp(articleReference.articleNumber)) {
        return undefined;
    }

    const findMainPrefab = (prefabs: PrefabModel[]): PrefabModel | undefined => {
        for (const prefab of prefabs) {
            if (prefab.isMain) {
                return prefab;
            }

            const childMain = findMainPrefab(prefab.children);
            if (childMain) {
                return childMain;
            }
        }

        return undefined;
    };

    return findMainPrefab(articleReference.articleRepresentations)?.name;
}

export function shouldUseZAxisOnlyDrag(selectedInstanceIds: readonly string[], state: PlacementState): boolean {
    if (selectedInstanceIds.length === 0) {
        return false;
    }

    const poiGuidToFilterType = new Map<string, POIFilterType>();
    for (const poiData of state.pois) {
        if (poiData.filterType !== undefined) {
            poiGuidToFilterType.set(poiData.poi.poiGuid, poiData.filterType);
        }
    }

    const findInstance = (instanceId: string): ArticleInstance | undefined => {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (articleId) {
            const instance = state.articles[articleId]?.articleInstances[instanceId];
            if (instance) {
                return instance;
            }
        }
        for (const article of Object.values(state.articles)) {
            const instance = article.articleInstances[instanceId];
            if (instance) {
                return instance;
            }
        }
        return undefined;
    };

    let referencePoiGuids: Set<string> | null = null;

    for (const instanceId of selectedInstanceIds) {
        const instanceData = findInstance(instanceId);
        const poiGuids = (instanceData?.poiGuids ?? []).filter((guid) => guid && guid !== EMPTY_GUID);

        if (poiGuids.some((guid) => poiGuidToFilterType.get(guid) === POIFilterType.Corner)) {
            return true;
        }

        const currentPoiGuids = new Set(poiGuids);
        if (!referencePoiGuids) {
            referencePoiGuids = currentPoiGuids;
            continue;
        }

        if (
            currentPoiGuids.size !== referencePoiGuids.size ||
            [...currentPoiGuids].some((guid) => !referencePoiGuids!.has(guid))
        ) {
            return true;
        }
    }

    return false;
}

export function getAnchorAlignment(instance: ArticleInstance, target: Vector3): boolean {
    const parts = Object.values(instance.parts);
    if (parts.length === 0) {
        return false;
    }

    target.set(0, 0, 0);
    for (const part of parts) {
        target.add(part.alignment ?? new Vector3(0, 0, 1));
    }

    target.normalize();
    return true;
}

export function getAnchorDragContext(
    anchorId: string | undefined,
    dragRefsMap: Map<string, ArticleDragReference>
): { anchorArticleId?: string; anchorDragRef?: ArticleDragReference; anchorInstanceData?: ArticleInstance } {
    if (!anchorId) {
        return {};
    }

    const anchorArticleId = instanceToArticleIdMap.get(anchorId);
    const anchorDragRef = anchorArticleId ? dragRefsMap.get(anchorArticleId) : undefined;
    const anchorInstanceData = anchorDragRef?.articleRef.current?.articleInstances[anchorId];

    return { anchorArticleId, anchorDragRef, anchorInstanceData };
}

export function getAnchorPartName(instance: ArticleInstance | null | undefined): string | null {
    if (!instance) {
        return null;
    }

    const partKeys = Object.keys(instance.parts);
    if (partKeys.length === 0) {
        return null;
    }

    return partKeys.length === 1 ? partKeys[0] : (partKeys.find((key) => key.length === 8) ?? partKeys[0]);
}

export function getAnchorPart(instance: ArticleInstance | null | undefined, preferredPartName?: string): Part | null {
    if (!instance) {
        return null;
    }

    const anchorPartKey =
        (preferredPartName && instance.parts[preferredPartName] ? preferredPartName : getAnchorPartName(instance)) ??
        undefined;
    if (!anchorPartKey) {
        return null;
    }

    const anchorPart = instance.parts[anchorPartKey];
    if (!anchorPart) {
        return null;
    }

    return {
        alignment: anchorPart.alignment.clone(),
        position: anchorPart.position.clone(),
        rotation: anchorPart.rotation,
        scale: anchorPart.scale.clone()
    };
}

export function translateInstanceParts(instance: ArticleInstance, delta: Vector3): void {
    for (const part of Object.values(instance.parts)) {
        part.position.add(delta);
    }
}

export function translateDraggingBox(draggingBox: DraggingBox, delta: Vector3): void {
    const e = draggingBox.localToWorld.elements;
    e[12] += delta.x;
    e[13] += delta.y;
    e[14] += delta.z;
}

export function projectDeltaToWorkingPlane(delta: Vector3, workingPlaneNormal: Vector3): void {
    delta.addScaledVector(workingPlaneNormal, -delta.dot(workingPlaneNormal));
}

/**
 * Returns true when the selected instances span more than one wall alignment,
 * meaning a rigid-group drag should be used instead of per-wall constraint.
 */
export function isMixedAlignmentSelection(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>
): boolean {
    const firstAlignment = new Vector3();
    const currentAlignment = new Vector3();
    let hasFirst = false;

    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instanceData = dragRef?.articleRef.current?.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        if (!getAnchorAlignment(instanceData, hasFirst ? currentAlignment : firstAlignment)) {
            continue;
        }

        if (!hasFirst) {
            hasFirst = true;
            continue;
        }

        if (Math.abs(firstAlignment.dot(currentAlignment)) < ALIGNMENT_PARALLEL_THRESHOLD) {
            return true;
        }
    }

    return false;
}

export function isTopBottomCameraView(cameraDirection: Vector3): boolean {
    return Math.abs(cameraDirection.z) > TOP_BOTTOM_VIEW_THRESHOLD;
}

export function alignSelectionToCursorPoint(
    state: PlacementState,
    dragPoint: Vector3,
    snapAnchorInstanceId: string | undefined,
    constraintNormal: Vector3 | null = null,
    targetPartName?: string
): boolean {
    const selectedInstanceIds = Object.keys(state.articleInstanceSelections);
    if (selectedInstanceIds.length === 0) {
        return false;
    }

    updateInstanceToArticleIdMap(selectedInstanceIds, state);

    const dragRefsMap = state.articleDragRefsMap.current;
    const anchorId =
        snapAnchorInstanceId && selectedInstanceIds.includes(snapAnchorInstanceId)
            ? snapAnchorInstanceId
            : selectedInstanceIds[0];

    const { anchorArticleId, anchorInstanceData } = getAnchorDragContext(anchorId, dragRefsMap);
    const anchorInstance =
        anchorInstanceData ??
        (anchorArticleId ? state.articles[anchorArticleId]?.articleInstances[anchorId] : undefined);

    const mainPartName = getConnectorMainPartName(anchorArticleId, dragRefsMap);
    // Paste keeps the first-part anchor used to build the clipboard payload.
    const cursorPartName =
        targetPartName ??
        (state.isAutoDragActiveRef.current && !state.isDragNewArticleActiveRef.current && anchorInstance
            ? Object.keys(anchorInstance.parts)[0]
            : undefined);
    if (!anchorInstance || !getAnchorPosition(anchorInstance, anchorPosition, mainPartName, cursorPartName)) {
        return false;
    }

    let moved = false;
    const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);

    targetPosition.copy(anchorPosition);

    if (state.isDragNewArticleActiveRef.current && !targetPartName) {
        const insertionPoint = anchorArticleId
            ? state.computeArticleInsertionPoint(anchorArticleId, anchorInstance)
            : null;
        if (!insertionPoint) {
            return false;
        }
        targetPosition.copy(insertionPoint);
    }

    dragDelta.subVectors(dragPoint, targetPosition);

    if (state.isZAxisDragActiveRef.current) {
        dragDelta.x = 0;
        dragDelta.y = 0;
    }

    if (isTopBottomView) {
        dragDelta.z = 0;
    }

    if (constraintNormal) {
        projectDeltaToWorkingPlane(dragDelta, constraintNormal);
    }

    if (dragDelta.x === 0 && dragDelta.y === 0 && dragDelta.z === 0) {
        return false;
    }

    const hasPerInstanceOffsets = perInstanceDragOffsets.size > 0;

    for (const instanceId of selectedInstanceIds) {
        const { anchorArticleId: instanceArticleId, anchorInstanceData: instanceFromRef } = getAnchorDragContext(
            instanceId,
            dragRefsMap
        );
        const instanceData =
            instanceFromRef ??
            (instanceArticleId ? state.articles[instanceArticleId]?.articleInstances[instanceId] : undefined);
        if (!instanceData) {
            continue;
        }

        let delta = dragDelta;

        // Compute per-instance delta
        if (hasPerInstanceOffsets && instanceId !== anchorId) {
            const grabOffset = perInstanceDragOffsets.get(instanceId);
            if (
                grabOffset &&
                getAnchorPosition(
                    instanceData,
                    perInstancePosition,
                    getConnectorMainPartName(instanceArticleId, dragRefsMap)
                )
            ) {
                instanceDragDelta.copy(dragPoint).sub(grabOffset).sub(perInstancePosition);
                delta = instanceDragDelta;

                if (state.isZAxisDragActiveRef.current) {
                    delta.x = 0;
                    delta.y = 0;
                }
                if (isTopBottomView) {
                    delta.z = 0;
                }

                if (getAnchorAlignment(instanceData, perInstanceAlignment)) {
                    projectDeltaToWorkingPlane(delta, perInstanceAlignment);
                } else if (constraintNormal) {
                    projectDeltaToWorkingPlane(delta, constraintNormal);
                }
            }
        }

        translateInstanceParts(instanceData, delta);

        // Keep cached snap colliders in sync
        const cached = dragColliderCache.get(instanceId);
        if (cached) {
            for (const p of cached) {
                p.add(delta);
            }
        }
        moved = true;
    }

    return moved;
}

export function placeInstanceOnWall(
    instanceId: string,
    articleId: string | null,
    state: PlacementState,
    dragRefsMap: Map<string, ArticleDragReference>
): void {
    const poiIndex = state.poiSelection.current.index;
    if (!instanceId || !articleId || poiIndex === undefined) {
        return;
    }

    // On top/bottom view insertion, snap the article to the bottom of the currently selected wall
    const articleDragRef = dragRefsMap.get(articleId);
    const instance = articleDragRef?.articleRef.current?.articleInstances[instanceId];
    const bottomZ = state.wallBottomZ;

    if (bottomZ !== undefined && instance && articleDragRef?.articleRef.current?.articleRepresentations) {
        const colliders = state.computeInstanceColliders(
            instanceId,
            instance,
            articleDragRef?.articleRef.current?.articleRepresentations,
            poiIndex
        );

        let newZ = bottomZ;
        if (colliders.length > 0) {
            const maxZ = Math.max(...colliders.map((c) => c.position.z));
            const minZ = Math.min(...colliders.map((c) => c.position.z));
            const height = maxZ - minZ;
            newZ = bottomZ + height / 2;
        }

        // Update z position of the instance parts and the raycast plane anchor point
        const dragInstance = articleDragRef?.articleRef.current?.articleInstances?.[instanceId];
        if (dragInstance) {
            if (state.isZPositionUpdatePending && state.faceSelectionRef.current) {
                boundingBoxHelper.setFromObject(state.faceSelectionRef.current);
                boundingBoxHelper.getCenter(wallCenter);
            }
            const parts = Object.values(dragInstance.parts);
            for (const part of parts) {
                if (state.isZPositionUpdatePending) {
                    part.position.x = wallCenter.x;
                    part.position.y = wallCenter.y;
                }
                part.position.z = newZ;
            }

            initialPoint.set(parts[0].position.x, parts[0].position.y, parts[0].position.z);
            raycastPlane.setFromNormalAndCoplanarPoint(raycastPlane.normal, initialPoint);
        }

        if (state.isZPositionUpdatePending) {
            const updatedArticleInstances: Array<{
                articleId: string;
                instanceId: string;
                instance: ArticleInstance;
            }> = [];

            updatedArticleInstances.push({ articleId, instanceId, instance: dragInstance });
            state.updateArticleInstances(updatedArticleInstances, false);
        }
    }
}

/* drag settings */
export function handleMarkerVisibility(markerRef: RefObject<Mesh | null>, isVisible: boolean) {
    if (markerRef.current) {
        markerRef.current.visible = isVisible;
    }
}

export function handleDragSettings(isDragStart: boolean, gl: RefObject<WebGLRenderer | null>) {
    if (gl.current) {
        gl.current.domElement.style.cursor = isDragStart ? 'move' : 'auto';
    }
}

/* instance grouping */
export function appendGroupedInstance(grouped: Map<string, string[]>, articleId: string, instanceId: string): void {
    const group = grouped.get(articleId);
    if (group) {
        group.push(instanceId);
    } else {
        grouped.set(articleId, [instanceId]);
    }
}

export function groupSelectedInstancesByArticle(selectedInstanceIds: string[]): Map<string, string[]> {
    const grouped = new Map<string, string[]>();
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        appendGroupedInstance(grouped, articleId, instanceId);
    }
    return grouped;
}

/* mappings */
export function updateInstanceToArticleIdMap(selectedInstanceIds: string[], state: PlacementState): void {
    for (const instanceId of selectedInstanceIds) {
        if (instanceToArticleIdMap.has(instanceId)) {
            continue;
        }
        let resolvedArticleId = state.selectedArticleId ?? undefined;
        if (!resolvedArticleId || !(instanceId in (state.articles[resolvedArticleId]?.articleInstances ?? {}))) {
            resolvedArticleId = Object.keys(state.articles).find(
                (articleId) => instanceId in state.articles[articleId].articleInstances
            );
        }
        if (resolvedArticleId) {
            instanceToArticleIdMap.set(instanceId, resolvedArticleId);
        }
    }
}

export function preUpdateInstanceToArticleIdMap(
    entries: ReadonlyArray<{ instanceId: string; articleId: string }>
): void {
    for (const { instanceId, articleId } of entries) {
        instanceToArticleIdMap.set(instanceId, articleId);
    }
}

export function updateInstanceToPoiIndexMap(selectedInstanceIds: string[], state: PlacementState): void {
    for (const instanceId of selectedInstanceIds) {
        if (instanceToPoiIndexMap.has(instanceId)) {
            continue;
        }
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        for (let i = 0; i < state.pois.length; i++) {
            const poiArticles = state.pois[i].articles?.[articleId];
            if (Array.isArray(poiArticles) && poiArticles.includes(instanceId)) {
                instanceToPoiIndexMap.set(instanceId, i);
                break;
            }
        }
    }
}

export function hasAnyRegisteredDragRefs(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>
): boolean {
    return (
        selectedInstanceIds.length > 0 &&
        selectedInstanceIds.some((id) => {
            const articleId = instanceToArticleIdMap.get(id);
            return articleId !== undefined && dragRefsMap.has(articleId);
        })
    );
}

/* dragging box */
export function scheduleDraggingBoxComputation(
    selectedInstanceIds: string[],
    anchorId: string,
    dragRefsMap: Map<string, ArticleDragReference>,
    draggingBox: DraggingBox,
    placementState?: PlacementState
): void {
    const anchorArticleId = instanceToArticleIdMap.get(anchorId);
    const anchorDragRef = anchorArticleId ? dragRefsMap.get(anchorArticleId) : undefined;
    const anchorInstanceData = anchorDragRef?.articleRef.current?.articleInstances[anchorId];

    const articleTypesMap = new Map<string, DraggingBoxWorkerInput['articleTypes'][0]>();
    const instancesForWorker: DraggingBoxWorkerInput['instances'] = [];
    const buildArticleTypeId = (articleId: string, partName: string) => `${articleId}::${partName}`;

    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        if (!dragRef) {
            continue;
        }

        // Use the geometry bounding box from the BatchedMesh geometry slots.
        if (placementState) {
            const slots = placementState.articleGeometrySlots.get(articleId!);
            const firstSlotId = slots?.physicalSlots[0]?.slotId;
            const batchMesh = placementState.batchedMeshes.physical;
            if (firstSlotId !== undefined && batchMesh) {
                const bbox = new Box3();
                batchMesh.getBoundingBoxAt(firstSlotId, bbox);
                if (!bbox.isEmpty()) {
                    const articleTypeId = buildArticleTypeId(articleId!, 'batch');
                    articleTypesMap.set(articleTypeId, {
                        id: articleTypeId,
                        matrixWorld: Array.from(new Matrix4Three().elements),
                        geomMin: { x: bbox.min.x, y: bbox.min.y, z: bbox.min.z },
                        geomMax: { x: bbox.max.x, y: bbox.max.y, z: bbox.max.z }
                    });
                }
            }
        }

        const instanceData = dragRef.articleRef.current?.articleInstances[instanceId];
        if (instanceData) {
            const batchArticleTypeId = buildArticleTypeId(articleId!, 'batch');

            for (const [partName, part] of Object.entries(instanceData.parts)) {
                const articleTypeId = articleTypesMap.has(batchArticleTypeId)
                    ? batchArticleTypeId
                    : buildArticleTypeId(articleId!, partName);
                if (!articleTypesMap.has(articleTypeId)) {
                    continue;
                }

                instancesForWorker.push({
                    position: {
                        x: part.position.x,
                        y: part.position.y,
                        z: part.position.z
                    },
                    alignment: {
                        x: part.alignment.x,
                        y: part.alignment.y,
                        z: part.alignment.z
                    },
                    scale: {
                        x: part.scale.x,
                        y: part.scale.y,
                        z: part.scale.z
                    },
                    rotation: part.rotation,
                    articleTypeId
                });
            }
        }
    }

    if (!anchorInstanceData || articleTypesMap.size === 0 || instancesForWorker.length === 0) {
        return;
    }

    // Use midpoint of all selected instances as anchor position so the
    // local coordinate frame is centered on the selection (matches transformation mesh).
    const midpoint = new Vector3();
    let midCount = 0;
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const dragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instData = dragRef?.articleRef.current?.articleInstances[instanceId];
        if (instData) {
            const pos = new Vector3();
            if (getAnchorPosition(instData, pos, getConnectorMainPartName(articleId, dragRefsMap))) {
                midpoint.add(pos);
                midCount++;
            }
        }
    }

    const anchorPositionForWorker = new Vector3();
    const anchorAlignment = new Vector3();
    if (midCount > 0) {
        midpoint.divideScalar(midCount);
        anchorPositionForWorker.copy(midpoint);
    } else {
        getAnchorPosition(
            anchorInstanceData,
            anchorPositionForWorker,
            getConnectorMainPartName(anchorArticleId, dragRefsMap)
        );
    }
    getAnchorAlignment(anchorInstanceData, anchorAlignment);

    let anchorRotation = 0;
    const anchorFirstPart = Object.values(anchorInstanceData.parts)[0];
    if (anchorFirstPart) {
        anchorRotation = anchorFirstPart.rotation;
    }

    draggingBoxStatus.isPending = true;

    postDraggingBoxTask({
        type: 'computeDraggingBox',
        articleTypes: Array.from(articleTypesMap.values()),
        instances: instancesForWorker,
        anchor: {
            position: { x: anchorPositionForWorker.x, y: anchorPositionForWorker.y, z: anchorPositionForWorker.z },
            alignment: { x: anchorAlignment.x, y: anchorAlignment.y, z: anchorAlignment.z },
            rotation: anchorRotation
        }
    })
        .then((result) => {
            draggingBoxStatus.isPending = false;
            draggingBoxStatus.isComputed = true;

            if (!result.isEmpty) {
                draggingBox.boundingBox.set(
                    new Vector3(result.localMin.x, result.localMin.y, result.localMin.z),
                    new Vector3(result.localMax.x, result.localMax.y, result.localMax.z)
                );
                draggingBox.localToWorld.fromArray(result.localToWorld);
            }

            invalidate();
        })
        .catch((e) => {
            if (e instanceof DOMException && e.name === 'AbortError') {
                draggingBoxStatus.isPending = false;
                return;
            }

            draggingBoxStatus.isPending = false;
            draggingBoxStatus.isComputed = true;

            // eslint-disable-next-line no-console
            console.error('DraggingBox worker error', e);
        });
}

/**
 * Seeds per-instance wall planes and intersection points for all selected instances.
 */
export function initializePerInstancePlanesAndIntersections(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>,
    eventRay: Ray,
    perInstancePlanes: Map<string, Plane>,
    perInstancePrevIntersections: Map<string, Vector3>
) {
    const initAlign = new Vector3();
    const initPos = new Vector3();
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        const dragRef = dragRefsMap.get(articleId);
        if (!dragRef || !dragRef.articleRef.current) {
            continue;
        }
        const instanceData = dragRef.articleRef.current.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }
        if (
            getAnchorAlignment(instanceData, initAlign) &&
            getAnchorPosition(instanceData, initPos, getConnectorMainPartName(articleId, dragRefsMap))
        ) {
            const wallPlane = new Plane().setFromNormalAndCoplanarPoint(initAlign, initPos);
            perInstancePlanes.set(instanceId, wallPlane);
            const initPt = new Vector3();
            if (eventRay.intersectPlane(wallPlane, initPt)) {
                perInstancePrevIntersections.set(instanceId, initPt);
            }
        }
    }
}

/**
 * Applies a movement delta to a single instance's parts and syncs its collider cache.
 */
export function applyMoveToInstance(instanceId: string, instanceData: ArticleInstance, delta: Vector3): void {
    translateInstanceParts(instanceData, delta);
    const cached = dragColliderCache.get(instanceId);
    if (cached) {
        for (const point of cached) {
            point.add(delta);
        }
    }
}

/**
 * Computes the per-instance wall-plane movement delta for object-based dragging.
 * Writes the result into `outDelta` and returns true if the delta is non-zero.
 * Updates perInstancePrevIntersections as a side effect.
 */
export function computePerInstanceWallDelta(
    instanceId: string,
    eventRay: Ray,
    isTopBottomViewDrag: boolean,
    fallbackDelta: Vector3,
    outDelta: Vector3
): boolean {
    const wallPlane = perInstancePlanes.get(instanceId);
    const prevPt = perInstancePrevIntersections.get(instanceId);

    if (wallPlane && !isTopBottomViewDrag && eventRay.intersectPlane(wallPlane, wallIntersectionPoint)) {
        if (prevPt) {
            outDelta.subVectors(wallIntersectionPoint, prevPt);
            prevPt.copy(wallIntersectionPoint);
        } else {
            perInstancePrevIntersections.set(instanceId, wallIntersectionPoint.clone());
            outDelta.set(0, 0, 0);
        }
    } else {
        outDelta.copy(fallbackDelta);
        if (wallPlane) {
            projectDeltaToWorkingPlane(outDelta, wallPlane.normal);
        }

        // Keep selection synchronized with cursor after leaving the camera frustum
        if (prevPt && (outDelta.x !== 0 || outDelta.y !== 0 || outDelta.z !== 0)) {
            prevPt.add(outDelta);
        }
    }

    return outDelta.x !== 0 || outDelta.y !== 0 || outDelta.z !== 0;
}

/**
 * Projects and applies a snap offset to all selected instances, respecting per-instance wall normals.
 * `rigidInstances` can be:
 *   - `true`  → all instance get dragged as a rigid group by applying uniform snap offsets (dragging-box / groups)
 *   - `false` → all instances get dragged individual along their own planes
 *   - `Set<string>` → only instances included in the Set get uniform snap offsets - others get individual offsets
 */
export function applySnapOffsetToInstances(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>,
    snapOffset: Vector3,
    perInstancePlanes: Map<string, Plane>,
    rigidInstances: Set<string> | boolean = false
) {
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        if (!articleId) {
            continue;
        }
        const dragRef = dragRefsMap.get(articleId);
        if (!dragRef || !dragRef.articleRef.current) {
            continue;
        }
        const instanceData = dragRef.articleRef.current.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        const isRigid = typeof rigidInstances === 'boolean' ? rigidInstances : rigidInstances.has(instanceId);

        if (isRigid) {
            // Apply uniform snap offset - not per instance
            applyMoveToInstance(instanceId, instanceData, snapOffset);
        } else {
            const plane = perInstancePlanes.get(instanceId);
            if (plane) {
                projectedSnapDelta.copy(snapOffset);
                projectDeltaToWorkingPlane(projectedSnapDelta, plane.normal);
                applyMoveToInstance(instanceId, instanceData, projectedSnapDelta);
            } else {
                applyMoveToInstance(instanceId, instanceData, snapOffset);
            }
        }
    }
}

/* Clamp Placement Helpers */
export async function waitForManualConnectorInsertion(articleId: string | null): Promise<boolean> {
    if (!articleId) {
        return false;
    }

    const currentState = usePlacementStore.getState();
    const poiIndex = currentState.poiSelection.current.index;
    const articleNumber = articleId ? currentState.articles[articleId]?.articleNumber : null;

    if (!articleNumber || !isFramiClamp(articleNumber)) {
        return true;
    }

    if (poiIndex === undefined) {
        return true;
    }

    await currentState.prepareManualConnectorDrag?.({
        articleId,
        articleNumber,
        poiIndex
    });

    const state = usePlacementStore.getState();
    return (
        state.selectedArticleId === articleId &&
        state.manualPlacementData.articleId === articleId &&
        !state.manualPlacementData.isLoading &&
        state.manualPlacementData.candidates.length > 0 &&
        state.manualPlacementData.plane !== null
    );
}

export function validateConnectorInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    void (async () => {
        const canStartInsertion = await waitForManualConnectorInsertion(articleId);
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    })();
}

export async function waitForManualAnchorInsertion(articleId: string | null): Promise<boolean> {
    if (!articleId) {
        return false;
    }

    const state = usePlacementStore.getState();
    const poiIndex = state.poiSelection.current.index;
    if (poiIndex === undefined) {
        return false;
    }

    const prepared = await state.prepareManualAnchorPlacement?.({ articleId, poiIndex });
    const currentState = usePlacementStore.getState();
    return (
        prepared === true &&
        currentState.selectedArticleId === articleId &&
        currentState.manualPlacementData.articleId === articleId &&
        currentState.manualPlacementData.settingType === SettingModelSettingType.Anchors &&
        !currentState.manualPlacementData.isLoading &&
        currentState.manualPlacementData.candidates.length > 0 &&
        currentState.manualPlacementData.plane !== null
    );
}

export function validateAnchorInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    void (async () => {
        if (isTopBottomCameraView(raycastPlane.normal)) {
            toastNotifications.information({ title: t('info'), message: t('infoAnchorPlacement') });
            return;
        }

        const canStartInsertion = await waitForManualAnchorInsertion(articleId);
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    })();
}

export async function waitForManualCornerInsertion(articleId: string | null): Promise<boolean> {
    if (!articleId) {
        return false;
    }

    const currentState = usePlacementStore.getState();
    const poiIndex = currentState.poiSelection.current.index;
    const articleNumber = articleId ? currentState.articles[articleId]?.articleNumber : null;

    if (!articleNumber) {
        return true;
    }

    if (poiIndex === undefined) {
        return true;
    }

    usePlacementStore.setState({ waitForPlacementData: true });
    await currentState.prepareManualCornerInsertion?.({
        articleId,
        articleNumber,
        poiIndex
    });

    const state = usePlacementStore.getState();
    return (
        state.selectedArticleId === articleId &&
        state.manualPlacementData.articleId === articleId &&
        !state.manualPlacementData.isLoading &&
        state.manualPlacementData.candidates.length > 0 &&
        state.manualPlacementData.plane !== null
    );
}

export function validateCornerInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    void (async () => {
        const canStartInsertion = await waitForManualCornerInsertion(articleId);
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    })();
}

function waitForArticleParts(articleId: string): Promise<boolean> {
    const state = usePlacementStore.getState();
    if ((state.articles[articleId]?.partNames.length ?? 0) > 0) {
        return Promise.resolve(true);
    }

    usePlacementStore.setState({ waitForPlacementData: true });

    return new Promise((resolve) => {
        const unsubscribe = usePlacementStore.subscribe((currentState) => {
            const isSelected = currentState.selectedArticleId === articleId;
            const hasParts = (currentState.articles[articleId]?.partNames.length ?? 0) > 0;

            if (hasParts || !isSelected) {
                unsubscribe();
                usePlacementStore.setState({ waitForPlacementData: false });
                resolve(hasParts && isSelected);
            }
        });
    });
}

export function validateArticleInsertionStart(isDoubleClick: boolean, articleId: string | null): void {
    if (!articleId) {
        return;
    }

    void waitForArticleParts(articleId).then((canStartInsertion) => {
        if (canStartInsertion) {
            usePlacementStore.getState().handleOnInsertArticleStart(isDoubleClick);
        }
    });
}

export function handleOnValidateInsertion(isDoubleClick: boolean, articleId: string | null): void {
    const state = usePlacementStore.getState();

    const isTopBottomView = isTopBottomCameraView(raycastPlane.normal);
    const articleNumber = articleId ? state.articles[articleId]?.articleNumber : null;
    const isConnectorInsertion = isFramiClamp(articleNumber);
    const isAnchorInsertion = isSupportingConstruction(articleNumber) || isFramiWedgeClamp(articleNumber);
    const isCornerInsertion = state.poiSelection.current.filterType === POIFilterType.Corner;

    if (isConnectorInsertion) {
        if (!isTopBottomView) {
            validateConnectorInsertionStart(isDoubleClick, articleId);
        } else {
            toastNotifications.information({ title: t('info'), message: t('infoClampPlacement') });
        }
    }

    if (isAnchorInsertion && !isCornerInsertion) {
        validateAnchorInsertionStart(isDoubleClick, articleId);
    }

    if (isCornerInsertion && !isConnectorInsertion && !isAnchorInsertion) {
        validateCornerInsertionStart(isDoubleClick, articleId);
    }

    if (!isConnectorInsertion && !isAnchorInsertion && !isCornerInsertion) {
        validateArticleInsertionStart(isDoubleClick, articleId);
    }
}

const connectorCandidatePlane = new Plane();
const connectorCandidateNormal = new Vector3();
const connectorCandidatePosition = new Vector3();
const connectorRayIntersection = new Vector3();

function getConnectorCandidateDistanceFromRay(ray: Ray, candidate: PartPlacement): number | null {
    connectorCandidateNormal.set(candidate.alignment.x, candidate.alignment.y, candidate.alignment.z);
    if (connectorCandidateNormal.lengthSq() <= EPSILON) {
        return null;
    }

    connectorCandidatePosition.set(candidate.position.x, candidate.position.y, candidate.position.z);
    connectorCandidatePlane.setFromNormalAndCoplanarPoint(
        connectorCandidateNormal.normalize(),
        connectorCandidatePosition
    );
    const intersection = ray.intersectPlane(connectorCandidatePlane, connectorRayIntersection);
    return intersection ? intersection.distanceTo(connectorCandidatePosition) : null;
}

export interface ResolvedConnectorSnapTarget {
    placement: PartPlacement;
    pairedPlacement?: PartPlacement | null;
    sourcePart: PartPlacement;
    sourceInstanceId: string;
    wallPoiId: string;
    poiGuids: string[];
    index: number;
    distance: number;
    rayDepth: number;
}

export function normalizeAdjustableConnectorSnapTarget(
    target: ResolvedConnectorSnapTarget,
    mainPartName: string
): ResolvedConnectorSnapTarget {
    if (target.placement.name === mainPartName || target.pairedPlacement?.name !== mainPartName) {
        return target;
    }

    return {
        ...target,
        placement: target.pairedPlacement,
        pairedPlacement: target.placement
    };
}

export function getAdditionalConnectorSnapFeedback(
    instance: ArticleInstance,
    target: ResolvedConnectorSnapTarget,
    captured: boolean
): SnapPointFeedback[] {
    const paired = target.pairedPlacement;
    const part = paired ? instance.parts[paired.name] : undefined;
    if (!paired || !part) {
        return [];
    }

    const position = new Vector3(paired.position.x, paired.position.y, paired.position.z);
    const primaryPosition = target.placement.position;
    if (
        position.distanceToSquared(new Vector3(primaryPosition.x, primaryPosition.y, primaryPosition.z)) <=
        EPSILON * EPSILON
    ) {
        return [];
    }
    return [{ target: position, source: captured ? undefined : part.position.clone() }];
}

export function alignAdjustableConnectorPartsToSnapTarget(
    instance: ArticleInstance,
    mainPartName: string,
    target: ResolvedConnectorSnapTarget
): boolean {
    if (!target.pairedPlacement) {
        return false;
    }

    const mainPlacement = target.placement.name === mainPartName ? target.placement : target.pairedPlacement;
    const secondaryPlacement = target.placement.name === mainPartName ? target.pairedPlacement : target.placement;
    const mainPart = instance.parts[mainPlacement.name];
    const secondaryPart = instance.parts[secondaryPlacement.name];
    if (!mainPart || !secondaryPart || mainPlacement.name !== mainPartName) {
        return false;
    }

    for (const [part, placement] of [
        [mainPart, mainPlacement],
        [secondaryPart, secondaryPlacement]
    ] as const) {
        part.position.set(placement.position.x, placement.position.y, placement.position.z);
        part.alignment.set(placement.alignment.x, placement.alignment.y, placement.alignment.z);
        part.rotation = placement.rotation;
    }

    return true;
}

export function refreshInstanceColliderCache(
    state: PlacementState,
    instanceId: string,
    instance: ArticleInstance,
    articleId: string,
    dragRefsMap: Map<string, ArticleDragReference>
): void {
    const representation = dragRefsMap.get(articleId)?.articleRef.current?.articleRepresentations;
    if (!representation?.length) {
        dragColliderCache.delete(instanceId);
        return;
    }

    const poiIndex = instanceToPoiIndexMap.get(instanceId) ?? state.poiSelection.current.index ?? 0;
    const colliders = state.computeInstanceColliders(instanceId, instance, representation, poiIndex);
    dragColliderCache.set(
        instanceId,
        colliders.map((collider) => collider.position.clone())
    );
}

const snapSegment = new Vector3();
const snapRelative = new Vector3();
const snapPoint = new Vector3();
const snapRayPoint = new Vector3();
const snapSegmentStart = new Vector3();
const snapSegmentEnd = new Vector3();
const snapCandidatePosition = new Vector3();

function closestPointOnSegment(source: Vector3, start: Vector3, end: Vector3, target: Vector3): number {
    snapSegment.subVectors(end, start);
    const segmentLengthSq = snapSegment.lengthSq();
    snapRelative.subVectors(source, start);
    const segmentT =
        segmentLengthSq > EPSILON ? Math.max(0, Math.min(1, snapRelative.dot(snapSegment) / segmentLengthSq)) : 0;
    target.copy(start).addScaledVector(snapSegment, segmentT);
    return source.distanceTo(target);
}

function isPointOnConnectorRange(point: Vector3, range: ConnectorSnapRange): boolean {
    const start = snapSegmentStart.set(range.startPoint.x, range.startPoint.y, range.startPoint.z);
    const end = snapSegmentEnd.set(range.endPoint.x, range.endPoint.y, range.endPoint.z);
    return closestPointOnSegment(point, start, end, snapPoint) <= CONNECTOR_POINT_RANGE_ALIGNMENT_TOLERANCE;
}

function closestRayPointOnSegment(
    ray: Ray,
    start: Vector3,
    end: Vector3,
    target: Vector3
): { distance: number; depth: number } {
    snapSegment.subVectors(end, start);
    snapRelative.subVectors(ray.origin, start);
    const segmentLengthSq = snapSegment.lengthSq();
    if (segmentLengthSq <= EPSILON) {
        target.copy(start);
        snapRelative.subVectors(target, ray.origin);
        const depth = Math.max(0, snapRelative.dot(ray.direction));
        snapRayPoint.copy(ray.origin).addScaledVector(ray.direction, depth);
        return { distance: snapRayPoint.distanceTo(target), depth };
    }

    const directionDotSegment = ray.direction.dot(snapSegment);
    const directionDotRelative = ray.direction.dot(snapRelative);
    const segmentDotRelative = snapSegment.dot(snapRelative);
    const denominator = segmentLengthSq - directionDotSegment * directionDotSegment;
    let segmentT =
        Math.abs(denominator) > EPSILON
            ? (segmentDotRelative - directionDotSegment * directionDotRelative) / denominator
            : segmentDotRelative / segmentLengthSq;
    segmentT = Math.max(0, Math.min(1, segmentT));
    target.copy(start).addScaledVector(snapSegment, segmentT);

    snapRelative.subVectors(target, ray.origin);
    let depth = Math.max(0, snapRelative.dot(ray.direction));
    if (depth === 0) {
        snapRelative.subVectors(ray.origin, start);
        segmentT = Math.max(0, Math.min(1, snapRelative.dot(snapSegment) / segmentLengthSq));
        target.copy(start).addScaledVector(snapSegment, segmentT);
        snapRelative.subVectors(target, ray.origin);
        depth = Math.max(0, snapRelative.dot(ray.direction));
    }
    snapRayPoint.copy(ray.origin).addScaledVector(ray.direction, depth);
    return { distance: snapRayPoint.distanceTo(target), depth };
}

function createResolvedConnectorSnapTarget(
    target: ConnectorSnapPoint | ConnectorSnapRange,
    index: number,
    position: Vector3,
    distance: number,
    rayDepth: number
): ResolvedConnectorSnapTarget {
    const pairedPlacement = 'pairedPlacement' in target ? target.pairedPlacement : undefined;
    const pairedEndPoint = 'pairedEndPoint' in target ? target.pairedEndPoint : undefined;
    let resolvedPairedPlacement = pairedPlacement;
    if (pairedPlacement && pairedEndPoint && 'startPoint' in target) {
        const start = snapSegmentStart.set(target.startPoint.x, target.startPoint.y, target.startPoint.z);
        const end = snapSegmentEnd.set(target.endPoint.x, target.endPoint.y, target.endPoint.z);
        snapSegment.subVectors(end, start);
        snapRelative.subVectors(position, start);
        const segmentLengthSq = snapSegment.lengthSq();
        const segmentT =
            segmentLengthSq > EPSILON ? Math.max(0, Math.min(1, snapRelative.dot(snapSegment) / segmentLengthSq)) : 0;
        resolvedPairedPlacement = {
            ...pairedPlacement,
            position: {
                x: pairedPlacement.position.x + (pairedEndPoint.x - pairedPlacement.position.x) * segmentT,
                y: pairedPlacement.position.y + (pairedEndPoint.y - pairedPlacement.position.y) * segmentT,
                z: pairedPlacement.position.z + (pairedEndPoint.z - pairedPlacement.position.z) * segmentT
            }
        };
    }

    return {
        placement: { ...target.placement, position: { x: position.x, y: position.y, z: position.z } },
        pairedPlacement: resolvedPairedPlacement,
        sourcePart: target.sourcePart,
        sourceInstanceId: target.sourceInstanceId,
        wallPoiId: target.wallPoiId,
        poiGuids: target.poiGuids,
        index,
        distance,
        rayDepth
    };
}

function resolveConnectorSnapTargetAtIndex(
    source: Vector3,
    ray: Ray,
    snapPoints: ConnectorSnapPoint[],
    snapRanges: ConnectorSnapRange[],
    index: number,
    useRay: boolean
): ResolvedConnectorSnapTarget | null {
    if (index < snapPoints.length) {
        const point = snapPoints[index];
        if (!point) {
            return null;
        }
        const position = snapCandidatePosition.set(
            point.placement.position.x,
            point.placement.position.y,
            point.placement.position.z
        );
        const distance = useRay
            ? getConnectorCandidateDistanceFromRay(ray, point.placement)
            : source.distanceTo(position);
        if (distance === null) {
            return null;
        }
        const rayDepth = useRay ? ray.origin.distanceTo(connectorRayIntersection) : 0;
        return createResolvedConnectorSnapTarget(point, index, position, distance, rayDepth);
    }

    const rangeIndex = index - snapPoints.length;
    const range = snapRanges[rangeIndex];
    if (!range) {
        return null;
    }
    const start = snapSegmentStart.set(range.startPoint.x, range.startPoint.y, range.startPoint.z);
    const end = snapSegmentEnd.set(range.endPoint.x, range.endPoint.y, range.endPoint.z);
    const rayResult = useRay ? closestRayPointOnSegment(ray, start, end, snapPoint) : null;
    const distance = rayResult?.distance ?? closestPointOnSegment(source, start, end, snapPoint);
    return createResolvedConnectorSnapTarget(range, index, snapPoint, distance, rayResult?.depth ?? 0);
}

const CONNECTOR_POINT_SNAP_PRIORITY_FACTOR = 3;
const CONNECTOR_POINT_RANGE_ALIGNMENT_TOLERANCE = 0.03;

export function getConnectorSnapCaptureDistance(zoom: number, baseDistance = 0.2): number {
    const normalizedZoom = Math.log10(Math.max(zoom, 1)) / Math.log10(9000);
    const zoomFactor = MathUtils.lerp(1, 0.1, MathUtils.clamp(normalizedZoom, 0, 1));
    return baseDistance * zoomFactor;
}

export function findNearestConnectorSnapTarget(
    source: Vector3,
    ray: Ray,
    snapPoints: ConnectorSnapPoint[],
    snapRanges: ConnectorSnapRange[],
    maxDistance: number,
    useRay: boolean,
    previousIndex: number | null = null
): ResolvedConnectorSnapTarget | null {
    let nearest: ResolvedConnectorSnapTarget | null = null;
    const targetCount = snapPoints.length + snapRanges.length;

    for (let index = 0; index < targetCount; index++) {
        const candidateIsPoint = index < snapPoints.length;
        const captureDistance = maxDistance * (candidateIsPoint ? SNAP_POINT_CAPTURE_FACTOR : 1);
        const candidate = resolveConnectorSnapTargetAtIndex(source, ray, snapPoints, snapRanges, index, useRay);
        if (!candidate || !Number.isFinite(candidate.distance) || candidate.distance > captureDistance) {
            continue;
        }

        const candidateRange = candidateIsPoint ? null : snapRanges[index - snapPoints.length];
        const nearestPoint = nearest && nearest.index < snapPoints.length ? snapPoints[nearest.index] : null;
        const nearestRange =
            nearest && nearest.index >= snapPoints.length ? snapRanges[nearest.index - snapPoints.length] : null;
        if (candidateRange && nearestPoint) {
            const pointPosition = snapCandidatePosition.set(
                nearestPoint.placement.position.x,
                nearestPoint.placement.position.y,
                nearestPoint.placement.position.z
            );
            if (isPointOnConnectorRange(pointPosition, candidateRange)) {
                continue;
            }
        }
        if (candidateIsPoint && nearestRange) {
            const point = snapPoints[index];
            if (
                point &&
                isPointOnConnectorRange(
                    snapCandidatePosition.set(
                        point.placement.position.x,
                        point.placement.position.y,
                        point.placement.position.z
                    ),
                    nearestRange
                )
            ) {
                nearest = candidate;
                continue;
            }
        }

        const candidatePriorityDistance =
            candidate.distance / (candidateIsPoint ? CONNECTOR_POINT_SNAP_PRIORITY_FACTOR : 1);
        const nearestIsPoint = nearest !== null && nearest.index < snapPoints.length;
        const nearestPriorityDistance = nearest
            ? nearest.distance / (nearestIsPoint ? CONNECTOR_POINT_SNAP_PRIORITY_FACTOR : 1)
            : Number.POSITIVE_INFINITY;
        if (
            candidatePriorityDistance < nearestPriorityDistance ||
            (Math.abs(candidatePriorityDistance - nearestPriorityDistance) < 0.0001 &&
                ((candidateIsPoint && !nearestIsPoint) ||
                    (candidateIsPoint === nearestIsPoint &&
                        candidate.rayDepth < (nearest?.rayDepth ?? Number.POSITIVE_INFINITY))))
        ) {
            nearest = candidate;
        }
    }

    const latched =
        previousIndex !== null
            ? resolveConnectorSnapTargetAtIndex(source, ray, snapPoints, snapRanges, previousIndex, useRay)
            : null;
    const releaseDistance =
        maxDistance *
        (latched && latched.index < snapPoints.length ? SNAP_POINT_CAPTURE_FACTOR : 1) *
        SNAP_RELEASE_FACTOR;
    if (!latched || !Number.isFinite(latched.distance) || latched.distance > releaseDistance) {
        return nearest;
    }
    if (!nearest) {
        return latched;
    }

    if (nearest.index === previousIndex) {
        return latched;
    }

    const nearestIsPoint = nearest.index < snapPoints.length;
    const latchedIsPoint = latched.index < snapPoints.length;
    if (nearestIsPoint !== latchedIsPoint) {
        return nearestIsPoint ? nearest : latched;
    }

    return nearest.distance < latched.distance * SNAP_SWITCH_FACTOR ? nearest : latched;
}

export function getConnectorSnapTargetAtIndex(
    source: Vector3,
    ray: Ray,
    snapPoints: ConnectorSnapPoint[],
    snapRanges: ConnectorSnapRange[],
    index: number,
    useRay: boolean
): ResolvedConnectorSnapTarget | null {
    return resolveConnectorSnapTargetAtIndex(source, ray, snapPoints, snapRanges, index, useRay);
}

export function createConnectorSnapPlane(placement: PartPlacement): Plane | null {
    const normal = new Vector3(placement.alignment.x, placement.alignment.y, placement.alignment.z);
    if (normal.lengthSq() <= EPSILON) {
        return null;
    }

    const anchor = new Vector3(placement.position.x, placement.position.y, placement.position.z);
    return new Plane().setFromNormalAndCoplanarPoint(normal.normalize(), anchor);
}

export function applyAlignmentToSelectedInstances(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>,
    alignment: Vector3
): void {
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const articleDragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instanceData = articleDragRef?.articleRef.current?.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        for (const part of Object.values(instanceData.parts)) {
            part.alignment.copy(alignment).normalize();
        }
    }
}

export function applyRotationToSelectedInstances(
    selectedInstanceIds: string[],
    dragRefsMap: Map<string, ArticleDragReference>,
    rotation: number
): void {
    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const articleDragRef = articleId ? dragRefsMap.get(articleId) : undefined;
        const instanceData = articleDragRef?.articleRef.current?.articleInstances[instanceId];
        if (!instanceData) {
            continue;
        }

        for (const part of Object.values(instanceData.parts)) {
            part.rotation = rotation;
        }
    }
}

export function getConnectorArticleId(state: PlacementState, anchorInstanceId?: string): string | null {
    if (state.isDragNewArticleStartRef.current || state.isDragNewArticleActiveRef.current) {
        return state.selectedArticleId;
    }

    if (anchorInstanceId) {
        const articleId = instanceToArticleIdMap.get(anchorInstanceId) ?? getArticleId(anchorInstanceId, state);
        if (articleId) {
            return articleId;
        }
    }

    return state.selectedArticleId;
}

export function getManualPlacementPlane(state: PlacementState, activeArticleId: string | null) {
    return activeArticleId !== null &&
        state.manualPlacementData.articleId === activeArticleId &&
        state.manualPlacementData.candidates.length > 0
        ? state.manualPlacementData.plane
        : null;
}

export function getConnectorSnapSource(
    dragPoint: Vector3,
    manualInsertionPlane: Plane | null,
    workingPlane: Plane | null
): Vector3 {
    adjustedSnapSource.copy(dragPoint);
    if (manualInsertionPlane) {
        manualInsertionPlane.projectPoint(adjustedSnapSource, adjustedSnapSource);
    } else if (workingPlane) {
        workingPlane.projectPoint(adjustedSnapSource, adjustedSnapSource);
    }
    return adjustedSnapSource;
}

export function getPreferredInsertionPlane(state: PlacementState, manualInsertionPlane: Plane | null): Plane {
    return isTopBottomCameraView(raycastPlane.normal)
        ? raycastPlane
        : (manualInsertionPlane ?? state.workingPlane ?? raycastPlane);
}

export function handleOnInsertionStart(
    isDoubleClick: boolean,
    articleId: string | null,
    t: TFunction<'translations', undefined>
) {
    const { interactionMode, isViewMode } = usePlacementStore.getState();
    if (interactionMode === InteractionMode.POI_NAVIGATION || isViewMode) {
        usePlacementStore.getState().setIsDragNewArticleStartRef(false);
        requestAnimationFrame(() => {
            toastNotifications.information({
                title: t('info'),
                message: t(isViewMode ? 'infoViewModeEditingDisabled' : 'infoDisabledPlacement')
            });
        });
    } else {
        handleOnValidateInsertion(isDoubleClick, articleId);
    }
}

/* Handle POI selection change during auto drag */

export const retargetAnchorPoint = new Vector3();
export const retargetNormal = new Vector3();
export const retargetProjectedPoint = new Vector3();

const retargetQuaternion = new Quaternion();
const retargetPlane = new Plane();
const retargetWorldUp = new Vector3(0, 0, 1);
const retargetWorldRight = new Vector3(1, 0, 0);
const retargetAxis = new Vector3();
const retargetSourceAlignment = new Vector3();
const retargetPartQuaternion = new Quaternion();
const retargetNewAlignQuaternion = new Quaternion();
const retargetRollQuaternion = new Quaternion();
const retargetNewAlignment = new Vector3();
const retargetPreviousQuaternion = new Quaternion();
const retargetCurrentQuaternion = new Quaternion();
const retargetDeltaQuaternion = new Quaternion();
const retargetPartDeltaQuaternion = new Quaternion();
const retargetAlignmentQuaternion = new Quaternion();
const retargetAlignment = new Vector3();

function updateQuaternionOnPoiChange(sourceAlignment: Vector3, targetNormal: Vector3): void {
    const alignmentDot = sourceAlignment.dot(targetNormal);

    switch (true) {
        case alignmentDot > 1 - EPSILON:
            retargetQuaternion.identity();
            return;
        case alignmentDot < -1 + EPSILON:
            retargetAxis.copy(retargetWorldUp).addScaledVector(sourceAlignment, -retargetWorldUp.dot(sourceAlignment));
            if (retargetAxis.lengthSq() < EPSILON) {
                retargetAxis
                    .copy(retargetWorldRight)
                    .addScaledVector(sourceAlignment, -retargetWorldRight.dot(sourceAlignment));
            }
            retargetQuaternion.setFromAxisAngle(retargetAxis.normalize(), Math.PI);
            return;
        default:
            retargetQuaternion.setFromUnitVectors(sourceAlignment, targetNormal);
    }
}

export function updateInstancesOnPoiChange(state: PlacementState, selectedInstanceIds: string[]): void {
    const grouped = groupSelectedInstancesByArticle(selectedInstanceIds);
    for (const [articleId, instanceIds] of grouped) {
        const dragRef = state.articleDragRefsMap.current.get(articleId);
        if (dragRef) {
            state.updateInstanceMatrices(true, dragRef.articleRef, false, instanceIds);
        }
    }
}

export function updateAlignmentOnPoiChange(
    state: PlacementState,
    selectedInstanceIds: string[],
    targetNormal: Vector3,
    anchorPoint: Vector3
): void {
    const dragRefsMap = state.articleDragRefsMap.current;
    updateInstanceToArticleIdMap(selectedInstanceIds, state);

    for (const instanceId of selectedInstanceIds) {
        const articleId = instanceToArticleIdMap.get(instanceId);
        const instanceData = articleId
            ? dragRefsMap.get(articleId)?.articleRef.current?.articleInstances[instanceId]
            : undefined;
        if (!instanceData) {
            continue;
        }

        const parts = Object.values(instanceData.parts);
        const sourceAlignment = parts[0]?.alignment;
        if (!sourceAlignment) {
            continue;
        }

        retargetSourceAlignment.copy(sourceAlignment).normalize();
        updateQuaternionOnPoiChange(retargetSourceAlignment, targetNormal);

        for (const part of parts) {
            updateFullQuaternion(part.alignment, part.rotation, retargetPartQuaternion);
            retargetPartQuaternion.premultiply(retargetQuaternion);

            part.position.sub(anchorPoint).applyQuaternion(retargetQuaternion).add(anchorPoint);
            retargetNewAlignment.copy(part.alignment).applyQuaternion(retargetQuaternion).normalize();
            part.alignment.copy(retargetNewAlignment);

            updateFullQuaternion(retargetNewAlignment, 0, retargetNewAlignQuaternion);
            retargetRollQuaternion.copy(retargetNewAlignQuaternion).invert();
            retargetRollQuaternion.premultiply(retargetPartQuaternion);
            part.rotation = decomposeRollAroundAxis(retargetRollQuaternion, retargetNewAlignment);
        }
    }
}

export function getAnchorPointOnPoiChange(
    state: PlacementState,
    selectedInstanceIds: string[],
    target: Vector3,
    snapAnchorInstanceId: string | undefined
): boolean {
    const anchorId =
        snapAnchorInstanceId && selectedInstanceIds.includes(snapAnchorInstanceId)
            ? snapAnchorInstanceId
            : selectedInstanceIds[0];
    if (!anchorId) {
        return false;
    }

    updateInstanceToArticleIdMap(selectedInstanceIds, state);
    const articleId = instanceToArticleIdMap.get(anchorId);
    const instanceData = articleId
        ? state.articleDragRefsMap.current.get(articleId)?.articleRef.current?.articleInstances[anchorId]
        : undefined;
    if (!instanceData) {
        return false;
    }

    const insertionPoint = articleId ? state.computeArticleInsertionPoint(articleId, instanceData) : null;
    if (insertionPoint) {
        target.copy(insertionPoint);
        return true;
    }

    return getAnchorPosition(
        instanceData,
        target,
        getConnectorMainPartName(articleId, state.articleDragRefsMap.current)
    );
}

export function getWallTargetPoint(state: PlacementState, targetPlane: Plane, target: Vector3): void {
    const raycaster = state.mainRaycasterRef.current;
    if (raycaster?.ray.intersectPlane(targetPlane, target)) {
        return;
    }

    const sourcePoint = state.pointerPositionRef.current ?? dragAnchorPositionRef.current ?? retargetAnchorPoint;
    targetPlane.projectPoint(sourcePoint, target);
}

export function getWallTargetPlane(state: PlacementState, poiIndex: number): Plane | null {
    const poi = state.pois[poiIndex]?.poi;
    if (!poi || !isPlanarFacePoi(poi)) {
        return null;
    }

    const firstPoint = poi.formFace.outerWire.edges[0]?.startPoint;
    if (!firstPoint) {
        return null;
    }

    retargetNormal.set(poi.formFace.normal.x, poi.formFace.normal.y, poi.formFace.normal.z).normalize();
    retargetAnchorPoint.set(firstPoint.x, firstPoint.y, firstPoint.z);
    return retargetPlane.setFromNormalAndCoplanarPoint(retargetNormal, retargetAnchorPoint);
}

export function updateReferencesOnPoiChange(
    state: PlacementState,
    selectedInstanceIds: string[],
    poiIndex: number
): void {
    const relatedPoiGuids = getRelatedPoiIndices(state.pois, poiIndex)
        .map((relatedPoiIndex) => state.pois[relatedPoiIndex]?.poi.poiGuid)
        .filter((poiGuid): poiGuid is string => Boolean(poiGuid));
    if (relatedPoiGuids.length === 0) {
        return;
    }

    activePoiGuids.clear();
    relatedPoiGuids.forEach((poiGuid) => activePoiGuids.add(poiGuid));
    updateInstanceToArticleIdMap(selectedInstanceIds, state);

    for (const instanceId of selectedInstanceIds) {
        instanceToPoiIndexMap.set(instanceId, poiIndex);
        dragColliderCache.delete(instanceId);
        const articleId = instanceToArticleIdMap.get(instanceId);
        const instance = articleId
            ? state.articleDragRefsMap.current.get(articleId)?.articleRef.current?.articleInstances[instanceId]
            : undefined;
        if (instance) {
            instance.poiGuids = relatedPoiGuids;
        }
    }
}

export function getCornerPartDelta(
    previousAnchorPart: Part | undefined,
    currentAnchorPart: Part | undefined,
    fallbackDelta: Quaternion
): Quaternion {
    const partDelta = retargetPartDeltaQuaternion;
    if (!previousAnchorPart || !currentAnchorPart) {
        return partDelta.copy(fallbackDelta);
    }

    updateFullQuaternion(previousAnchorPart.alignment, previousAnchorPart.rotation, retargetPreviousQuaternion);
    updateFullQuaternion(currentAnchorPart.alignment, currentAnchorPart.rotation, retargetCurrentQuaternion);
    return partDelta.copy(retargetPreviousQuaternion).invert().premultiply(retargetCurrentQuaternion);
}

export function applyCornerPartTransform(
    part: Part,
    previousAnchorPart: Part | undefined,
    currentAnchorPart: Part | undefined,
    previousAnchorPoint: Vector3,
    currentAnchorPoint: Vector3,
    fallbackDelta: Quaternion
): void {
    const partDelta = getCornerPartDelta(previousAnchorPart, currentAnchorPart, fallbackDelta);
    const pivotFrom = previousAnchorPart?.position ?? previousAnchorPoint;
    const pivotTo = currentAnchorPart?.position ?? currentAnchorPoint;

    updateFullQuaternion(part.alignment, part.rotation, retargetPartQuaternion);
    retargetPartQuaternion.premultiply(partDelta);
    part.position.sub(pivotFrom).applyQuaternion(partDelta).add(pivotTo);

    retargetAlignment.copy(part.alignment).applyQuaternion(partDelta).normalize();
    part.alignment.copy(retargetAlignment);
    updateFullQuaternion(retargetAlignment, 0, retargetAlignmentQuaternion);
    retargetRollQuaternion.copy(retargetAlignmentQuaternion).invert();
    retargetRollQuaternion.premultiply(retargetPartQuaternion);
    part.rotation = decomposeRollAroundAxis(retargetRollQuaternion, retargetAlignment);
}

export function retargetSelectionAtCorner(
    state: PlacementState,
    selectedInstanceIds: string[],
    anchorInstanceId: string | undefined,
    previousAnchorParts: Record<string, Part>,
    currentAnchorParts: Record<string, Part>,
    previousAnchorPoint: Vector3,
    currentAnchorPoint: Vector3,
    deltaQuaternion: Quaternion
): void {
    for (const instanceId of selectedInstanceIds) {
        if (instanceId === anchorInstanceId) {
            continue;
        }

        const articleId = instanceToArticleIdMap.get(instanceId);
        const instanceData = articleId
            ? state.articleDragRefsMap.current.get(articleId)?.articleRef.current?.articleInstances[instanceId]
            : undefined;
        if (!instanceData) {
            continue;
        }

        for (const [partName, part] of Object.entries(instanceData.parts)) {
            const previousAnchorPart = previousAnchorParts[partName];
            const currentAnchorPart = currentAnchorParts[partName];
            applyCornerPartTransform(
                part,
                previousAnchorPart,
                currentAnchorPart,
                previousAnchorPoint,
                currentAnchorPoint,
                deltaQuaternion
            );
        }
    }
}

export async function updateCornerPlacementTarget(
    state: PlacementState,
    getState: () => PlacementState,
    selectedInstanceIds: string[],
    poiIndex: number
): Promise<boolean> {
    const anchorInstanceId = selectedInstanceIds[0];
    updateInstanceToArticleIdMap(selectedInstanceIds, state);
    const articleId = instanceToArticleIdMap.get(anchorInstanceId ?? '') ?? state.selectedArticleId;
    if (!articleId) {
        return false;
    }

    const prepareCornerInsertion = state.prepareManualCornerInsertion;
    if (!prepareCornerInsertion) {
        return false;
    }

    const hasPreviousAnchorPoint = getAnchorPointOnPoiChange(
        state,
        selectedInstanceIds,
        retargetAnchorPoint,
        anchorInstanceId
    );
    if (!hasPreviousAnchorPoint) {
        return false;
    }
    const previousAnchorPoint = retargetAnchorPoint.clone();
    const previousPoiIndex = state.poiSelection.current.index;
    const previousManualPlacementData = state.manualPlacementData;
    const previousPointerPosition = state.pointerPositionRef.current?.clone() ?? null;
    const previousRaycastPlane = raycastPlane.clone();
    const restorePreviousTarget = () => {
        const latestState = getState();
        if (
            latestState.poiSelection.current.index !== previousPoiIndex ||
            latestState.manualPlacementData.isLoading ||
            (latestState.manualPlacementData.poiIndex !== poiIndex &&
                latestState.manualPlacementData.articleId !== null)
        ) {
            return;
        }

        usePlacementStore.setState({ manualPlacementData: previousManualPlacementData });
        latestState.pointerPositionRef.current = previousPointerPosition;
        raycastPlane.copy(previousRaycastPlane);
    };

    const canPrepareCorner = await prepareCornerInsertion({
        articleId,
        articleNumber: state.articles[articleId]?.articleNumber ?? null,
        poiIndex
    });
    if (!canPrepareCorner) {
        restorePreviousTarget();
        return false;
    }

    const currentState = getState();
    const { manualPlacementData } = currentState;
    const canInsertOnCorner =
        manualPlacementData.articleId === articleId &&
        manualPlacementData.poiIndex === poiIndex &&
        manualPlacementData.plane !== null &&
        manualPlacementData.candidates.length > 0;
    if (!canInsertOnCorner) {
        restorePreviousTarget();
        return false;
    }

    const articleDragRef = currentState.articleDragRefsMap.current.get(articleId);
    const anchorInstanceData = anchorInstanceId
        ? articleDragRef?.articleRef.current?.articleInstances[anchorInstanceId]
        : undefined;
    if (!anchorInstanceData) {
        restorePreviousTarget();
        return false;
    }

    const previousAnchorParts = cloneInstanceParts(anchorInstanceData.parts);
    const previousAnchorPart = getAnchorPart({ ...anchorInstanceData, parts: previousAnchorParts });
    if (!previousAnchorPart) {
        restorePreviousTarget();
        return false;
    }
    updateFullQuaternion(previousAnchorPart.alignment, previousAnchorPart.rotation, retargetPreviousQuaternion);

    const currentAnchorParts = mapPartsArrayToRecord(manualPlacementData.candidates);
    const previousAnchorPartName = getAnchorPartName({ ...anchorInstanceData, parts: previousAnchorParts });
    anchorInstanceData.parts = currentAnchorParts;
    const hasCurrentAnchorPoint = getAnchorPointOnPoiChange(
        currentState,
        selectedInstanceIds,
        retargetProjectedPoint,
        anchorInstanceId
    );
    if (!hasCurrentAnchorPoint) {
        anchorInstanceData.parts = previousAnchorParts;
        restorePreviousTarget();
        return false;
    }

    const currentAnchorPart = getAnchorPart(anchorInstanceData, previousAnchorPartName ?? undefined);
    if (!currentAnchorPart) {
        anchorInstanceData.parts = previousAnchorParts;
        restorePreviousTarget();
        return false;
    }
    updateFullQuaternion(currentAnchorPart.alignment, currentAnchorPart.rotation, retargetCurrentQuaternion);
    retargetDeltaQuaternion.copy(retargetPreviousQuaternion).invert();
    retargetDeltaQuaternion.premultiply(retargetCurrentQuaternion);

    retargetSelectionAtCorner(
        currentState,
        selectedInstanceIds,
        anchorInstanceId,
        previousAnchorParts,
        currentAnchorParts,
        previousAnchorPoint,
        retargetProjectedPoint,
        retargetDeltaQuaternion
    );

    currentState.setPointerPositionRef(retargetProjectedPoint);
    updateInstancesOnPoiChange(currentState, selectedInstanceIds);
    updateReferencesOnPoiChange(currentState, selectedInstanceIds, poiIndex);

    return true;
}

export function updateWallPlacementTarget(
    state: PlacementState,
    selectedInstanceIds: string[],
    poiIndex: number,
    snapAnchorInstanceId: string | undefined,
    options: { placementPlane?: Plane; persistPoiReference?: boolean } = {}
): boolean {
    const targetPlane = options.placementPlane ?? getWallTargetPlane(state, poiIndex);
    if (!targetPlane) {
        return false;
    }

    retargetNormal.copy(targetPlane.normal);
    if (options.persistPoiReference !== false) {
        state.setWorkingPlane(targetPlane.clone());
    }
    getWallTargetPoint(state, targetPlane, retargetProjectedPoint);
    raycastPlane.setFromNormalAndCoplanarPoint(retargetNormal, retargetProjectedPoint);
    state.setPointerPositionRef(retargetProjectedPoint);

    if (selectedInstanceIds.length === 0) {
        return true;
    }

    const hasRotationAnchor = getAnchorPointOnPoiChange(
        state,
        selectedInstanceIds,
        retargetAnchorPoint,
        snapAnchorInstanceId
    );
    updateAlignmentOnPoiChange(
        state,
        selectedInstanceIds,
        retargetNormal,
        hasRotationAnchor ? retargetAnchorPoint : retargetProjectedPoint
    );
    alignSelectionToCursorPoint(state, retargetProjectedPoint, snapAnchorInstanceId);
    syncDragStateOnPoiChange(selectedInstanceIds, targetPlane);
    updateInstancesOnPoiChange(state, selectedInstanceIds);
    if (options.persistPoiReference !== false) {
        updateReferencesOnPoiChange(state, selectedInstanceIds, poiIndex);
    }

    return true;
}

export function computeWorkingPlaneData(geometry: BufferGeometry): WorkingPlaneData {
    const pos = new Vector3().fromBufferAttribute(geometry.attributes.position as BufferAttribute, 0);
    const normal = new Vector3().fromBufferAttribute(geometry.attributes.normal as BufferAttribute, 0);

    const ref = Math.abs(normal.dot(new Vector3(0, 0, 1))) > 0.99 ? new Vector3(1, 0, 0) : new Vector3(0, 0, 1);
    const quaternion = new Quaternion().setFromUnitVectors(ref, normal.clone().normalize());

    return { position: pos, normal, quaternion };
}
